import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ActivityTypeDTO,
  AuditLogDTO,
  ExerciseSpecificClient,
  ExerciseSpecificDTO,
  ExerciseTypeClient,
  ExerciseTypeDTO,
  ExerciseTemplateDTO,
  FirearmSpecificClient,
  FirearmSpecificDTO,
  MemberDTO,
  MemberFirearmDTO,
  ScoreDTO,
  ScoreStatusDTO,
  ScoreTypeDTO,
  ScoreSectionDTO,
  CourseDTO,
} from '../services/apiClient';
import { useFormValidation, ValidationRules } from './useFormValidation';
import { useMemberFirearmApi } from './useMemberFirearmApi';
import { useExerciseTypeApi } from './useExerciseTypeApi';
import { useActivityTypes } from './useActivityTypeApi';
import { useFirearmSpecificWithExercisesById } from './useFirearmSpecificApi';
import { useExerciseTemplateApi } from './useExerciseTemplateApi';
import { useMemberApi } from './useMemberApi';
import { useScoreTypes } from './useScoreTypeApi';
import { useAuditLogApi } from './useAuditLogApi';
import { useScoreStatuses } from './useScoreStatusApi';
import { useFileApi } from './useFileApi';
import { useScoreApi } from './useScoreApi';
import { getMemberId, getRoles } from '../utils/tokenUtils';
import { ActivityTypeConstants } from '../constants/ActivityTypeConstants';
import { CourseTypeConstants } from '../constants/CourseTypeConstants';
import { ScoreTypeConstants } from '../constants/ScoreTypeConstants';
import { FilteredExerciseData } from '../utils/filterExerciseTypes';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

interface UseScoreFormLogicProps {
  initialValues: ScoreDTO;
  course?: CourseDTO;
  exerciseTypeFilter?: (
    firearmSpecific: FirearmSpecificDTO,
    course: CourseDTO,
    selectedFirearm: MemberFirearmDTO,
  ) => FilteredExerciseData; // Updated type
  scoreTypeConstant?: string;
}

export const useScoreFormLogic = ({
  initialValues,
  course,
  exerciseTypeFilter,
  scoreTypeConstant,
}: UseScoreFormLogicProps) => {
  const memberId = getMemberId();
  const roles = getRoles();

  // A score submission is a QR-deep-linked competition entry only when it's a Competition score
  // (never true for in-house/practical-evaluation forms, which pass a different scoreTypeConstant)
  // AND both exerciseTypeId and exerciseSpecificId were supplied up front by the caller. Organic
  // "Submit Score" clicks never populate those two fields, so this can't fire for them. Excludes
  // edits of an already-saved score (initialValues.id set) - those always have both fields
  // populated from the existing record, which would otherwise be mistaken for a QR deep-link and
  // wrongly lock the exercise selection / restrict the firearm list.
  const isQrCompetitionEntry =
    scoreTypeConstant === ScoreTypeConstants.COMPETITION &&
    !initialValues.id &&
    !!initialValues.exerciseTypeId &&
    !!initialValues.exerciseSpecificId;

  // State
  const [eligibleFirearmIds, setEligibleFirearmIds] = useState<string[] | null>(null);
  const [formData, setFormData] = useState<ScoreDTO>(initialValues);
  const [member, setMember] = useState<MemberDTO>();
  const [firearmSpecific, setFirearmSpecific] = useState<FirearmSpecificDTO>();
  const [selectedFirearmSpecificId, setSelectedFirearmSpecificId] = useState<
    string | undefined
  >(undefined);
  const [exerciseSpecifics, setExerciseSpecifics] = useState<
    ExerciseSpecificDTO[]
  >([]);
  const [exerciseTemplate, setExerciseTemplate] =
    useState<ExerciseTemplateDTO | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const [selectedFirearm, setSelectedFirearm] = useState<MemberFirearmDTO>();
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  // Dropdown data
  const [memberFirearms, setMemberFirearms] = useState<MemberFirearmDTO[]>([]);
  const [exerciseTypes, setExerciseTypes] = useState<ExerciseTypeDTO[]>([]);
  const [scoreStatuses, setScoreStatuses] = useState<ScoreStatusDTO[]>([]);
  const [filteredExerciseSpecifics, setFilteredExerciseSpecifics] = useState<ExerciseSpecificDTO[]>([]);

  // Audit logs
  const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);

  // API hooks
  const { uploadFile } = useFileApi();
  const { getFeedback } = useScoreApi();
  const { getById: getMemberById, isLoading: isLoadingMember } = useMemberApi();
  const { search: searchMemberFirearms, isLoading: isLoadingMemberFirearms } =
    useMemberFirearmApi();
  const { search: searchExerciseTypes, isLoading: isLoadingExerciseTypes } =
    useExerciseTypeApi();
  const { data: activityTypesData = [], isLoading: isLoadingActivityTypes } = useActivityTypes();
  const { data: scoreTypesData = [], isLoading: isLoadingScoreTypes } = useScoreTypes();
  const { data: scoreStatusesData, isLoading: isLoadingScoreStatuses } = useScoreStatuses();
  const {
    data: firearmSpecificWithExercises,
    isLoading: isFirearmSpecific,
  } = useFirearmSpecificWithExercisesById(selectedFirearmSpecificId);
  const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } =
    useAuditLogApi();
  const { getById: getExerciseTemplateById } = useExerciseTemplateApi();

  // Derived values
  const filteredActivityTypes = useMemo(() => {
    // For Course Practical Evaluations, filter by course type only (ignore exercise flags)
    if (scoreTypeConstant === ScoreTypeConstants.COURSE_PRACTICAL_EVALUATION && course?.courseTypeId) {
      if (course.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER) {
        return activityTypesData.filter(
          (type) => type.id === ActivityTypeConstants.HUNT_RELATED,
        );
      } else if (course.courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON) {
        return activityTypesData.filter(
          (type) => type.id === ActivityTypeConstants.SPORT_SHOOTING,
        );
      }
      // Fallback for other course types (if any)
      return activityTypesData;
    }

    // For regular scores, filter by exercise flags
    const exerciseSpecific = formData.exerciseSpecific;
    if (exerciseSpecific?.id && activityTypesData.length > 0) {
      const isAnyHunt = exerciseSpecific.isHuntActivity;
      const isAnySport = exerciseSpecific.isSportShootingActivity;

      if (isAnyHunt && isAnySport) {
        return activityTypesData.filter(
          (type) =>
            type.id === ActivityTypeConstants.HUNT_RELATED ||
            type.id === ActivityTypeConstants.SPORT_SHOOTING,
        );
      } else if (isAnyHunt) {
        return activityTypesData.filter(
          (type) => type.id === ActivityTypeConstants.HUNT_RELATED,
        );
      } else if (isAnySport) {
        return activityTypesData.filter(
          (type) => type.id === ActivityTypeConstants.SPORT_SHOOTING,
        );
      }
    }
    return activityTypesData;
  }, [formData.exerciseSpecific, activityTypesData, scoreTypeConstant, course]);

  // Determine if activity type should be disabled (for evaluations)
  const isActivityTypeDisabled = useMemo(() => {
    return scoreTypeConstant === ScoreTypeConstants.COURSE_PRACTICAL_EVALUATION && course?.courseTypeId !== undefined;
  }, [scoreTypeConstant, course]);

  // Validation rules
  const validationRules: ValidationRules = useMemo(() => {
    const baseRules: ValidationRules = {
      location: [{ required: true, message: 'Location is required' }],
      memberFirearmId: [
        { required: true, message: 'Member firearm is required' },
      ],
      exerciseTypeId: [
        { required: true, message: 'Exercise type is required' },
      ],
      exerciseSpecificId: [
        { required: true, message: 'Specific exercise is required' },
      ],
      activityTypeId: [
        { required: true, message: 'Activity type is required' },
      ],
      dateActivity: [{ required: true, message: 'Date activity is required' }],
    };

    formData.exerciseSpecific?.exerciseSpecificTargets?.forEach((target: any) => {
      baseRules[`target-${target.exerciseTargetId}`] = [
        {
          required: false,
          custom: (_value: any, data: ScoreDTO) => {
            return (data.scoreTargetFiles || []).some(
              (stf: any) => stf.exerciseTargetId === target.exerciseTargetId && stf.fileRecordId,
            );
          },
          message: `${target.exerciseTarget?.title || 'Target'} file is required`,
        },
      ];
    });

    // Validate that at least one score is greater than zero for EACH section
    // Both Count and HitMiss templates require at least one non-zero score per section
    if (exerciseTemplate) {
      baseRules.scoreSections = [
        {
          required: false,
          custom: (scoreSections: ScoreSectionDTO[]) => {
            if (!scoreSections || scoreSections.length === 0) {
              return { scoreSections: 'Please enter at least one score before submitting' };
            }

            // Check that EVERY section has at least one non-zero score
            const allSectionsHaveScore = scoreSections.every((section) => {
              // Skip sections with no fields (shouldn't happen, but defensive)
              if (!section.scoreFields || section.scoreFields.length === 0) {
                return true;
              }
              
              // Check if this section has at least one non-zero field value
              return section.scoreFields.some((field) => (field.value ?? 0) > 0);
            });

            if (!allSectionsHaveScore) {
              return { scoreSections: 'Please enter at least one score for each target before submitting' };
            }

            return true;
          },
          message: 'Please enter at least one score for each target before submitting',
        },
      ];
    }

    return baseRules;
  }, [formData.exerciseSpecific?.exerciseSpecificTargets, exerciseTemplate]);

  const { errors, validate, clearError } = useFormValidation<ScoreDTO>(validationRules);

  // Fetch functions
  const fetchMember = useCallback(async () => {
    try {
      const response = await getMemberById(memberId ?? formData.memberId ?? '');
      setMember(response?.data);
    } catch (error) {
      console.error('Error fetching member:', error);
    }
  }, []);

  const fetchMemberFirearms = useCallback(async () => {
    try {
      const response = await searchMemberFirearms({
        filters: {
          memberId: memberId || formData.memberId,
        },
        page: 1,
        pageSize: 9999,
        sortBy: '',
        sortDescending: false,
      });
      setMemberFirearms(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching member firearms:', error);
    }
  }, []);

  // File handling
  const handleFileChange = useCallback(
    (exerciseTargetId?: string) =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          const uploadKey = exerciseTargetId
            ? `target-${exerciseTargetId}`
            : 'fileAdditionalId';
          
          try {
            setUploadingFiles((prev) => ({ ...prev, [uploadKey]: true }));
            const response = await uploadFile(
              'files',
              file.name,
              file,
              'scores',
            );
            if (response && response.success && response.data) {
              if (exerciseTargetId) {
                setFormData((prev) => ({
                  ...prev,
                  scoreTargetFiles: [
                    ...(prev.scoreTargetFiles || []).filter(
                      (stf: any) => stf.exerciseTargetId !== exerciseTargetId,
                    ),
                    { exerciseTargetId, fileRecordId: response.data },
                  ],
                }));
                clearError(`target-${exerciseTargetId}`);
              } else {
                setFormData((prev) => ({ ...prev, fileAdditionalId: response.data }));
              }
            }
          } catch (error) {
            console.error('Error handling file:', error);
          } finally {
            setUploadingFiles((prev) => ({ ...prev, [uploadKey]: false }));
          }
        }
      },
    [uploadFile, clearError],
  );

  const getFileIdForTarget = useCallback(
    (exerciseTargetId: string): string | undefined => {
      const targetFile = formData.scoreTargetFiles?.find(
        (stf: any) => stf.exerciseTargetId === exerciseTargetId,
      );
      return targetFile?.fileRecordId;
    },
    [formData.scoreTargetFiles],
  );

  const handleScoreDataChange = useCallback(
    (
      scoreSections: ScoreSectionDTO[],
      grandTotals: { score: number; percentage: number; mark: number },
    ) => {
      setFormData((prev) => ({
        ...prev,
        templateId: exerciseTemplate?.id,
        scoreSections: scoreSections,
        grandTotalScore: grandTotals.score,
        grandTotalPercentage: grandTotals.percentage,
        grandTotalMark: grandTotals.mark,
      }));
    },
    [exerciseTemplate?.id],
  );

  // Effects
  useEffect(() => {
    setScoreStatuses(scoreStatusesData || []);
  }, [scoreStatusesData]);

  useEffect(() => {
    // Set scoreTypeId when scoreTypes data is loaded
    if (scoreTypesData.length > 0 && scoreTypeConstant) {
      const scoreType = scoreTypesData.find(
        (type: ScoreTypeDTO) => type.id === scoreTypeConstant,
      );
      if (scoreType) {
        setFormData((prev) => ({
          ...prev,
          scoreTypeId: scoreType.id,
        }));
      }
    }
  }, [scoreTypesData, scoreTypeConstant]);

  useEffect(() => {
    fetchMember();
    fetchMemberFirearms();
  }, []);

  // Deep-link prefill: for a QR-scanned competition target, work out which of the member's
  // firearms can shoot the exact (exerciseType, exerciseSpecific) pair printed on that target.
  // Auto-select only if exactly one qualifies; otherwise leave it for the member to pick from
  // the (now-restricted) firearm dropdown. Runs once.
  const eligibleFirearmCheckAttempted = useRef(false);
  useEffect(() => {
    if (eligibleFirearmCheckAttempted.current) return;
    if (!isQrCompetitionEntry) return;
    if (memberFirearms.length === 0) return;

    eligibleFirearmCheckAttempted.current = true;

    const findEligibleFirearms = async () => {
      const client = new FirearmSpecificClient(API_BASE_URL, axiosInstance);
      const eligible: string[] = [];

      await Promise.all(
        memberFirearms.map(async (firearm) => {
          if (!firearm.id || !firearm.firearmSpecificId) return;
          try {
            const response = await client.getFirearmSpecificByIdWithExercises(firearm.firearmSpecificId);
            const exercise = response?.data?.exercises?.find(
              (ex) => ex.exerciseTypeId === initialValues.exerciseTypeId && ex.exerciseType?.isCompetition === true,
            );
            const isEligible = !!exercise?.exerciseSpecifics?.some(
              (spec) => spec.id === initialValues.exerciseSpecificId && spec.isCompetition === true,
            );
            if (isEligible) {
              eligible.push(firearm.id);
            }
          } catch (error) {
            console.error('Error checking firearm eligibility for deep-linked exercise:', error);
          }
        }),
      );

      setEligibleFirearmIds(eligible);
      if (eligible.length === 1) {
        setFormData((prev) => ({ ...prev, memberFirearmId: eligible[0] }));
      }
    };

    findEligibleFirearms();
  }, [memberFirearms, isQrCompetitionEntry, initialValues.exerciseTypeId, initialValues.exerciseSpecificId]);

  // Deep-link prefill: load the exact exercise type/specific printed on the scanned target
  // directly by ID, independent of firearm selection, so the score form (and its scoring
  // template / target upload section) is fully populated the moment the drawer opens — the
  // member should only need to pick a firearm (if ambiguous), enter their score, and upload
  // the target file.
  const qrExerciseDataLoadAttempted = useRef(false);
  useEffect(() => {
    if (qrExerciseDataLoadAttempted.current) return;
    if (!isQrCompetitionEntry) return;

    qrExerciseDataLoadAttempted.current = true;

    const loadQrExerciseData = async () => {
      try {
        const exerciseTypeClient = new ExerciseTypeClient(API_BASE_URL, axiosInstance);
        const exerciseSpecificClient = new ExerciseSpecificClient(API_BASE_URL, axiosInstance);

        const [typeResponse, specificResponse] = await Promise.all([
          exerciseTypeClient.getExerciseTypeById(initialValues.exerciseTypeId!),
          exerciseSpecificClient.getExerciseSpecificById(initialValues.exerciseSpecificId!),
        ]);

        if (typeResponse?.data) {
          setExerciseTypes([typeResponse.data]);
        }
        if (specificResponse?.data) {
          setExerciseSpecifics([specificResponse.data]);
          setFormData((prev) => ({
            ...prev,
            exerciseSpecific: specificResponse.data,
            dateActivity: prev.dateActivity || (new Date().toISOString().split('T')[0] as unknown as Date),
          }));
        }
      } catch (error) {
        console.error('Error loading deep-linked exercise data:', error);
      }
    };

    loadQrExerciseData();
  }, [isQrCompetitionEntry, initialValues.exerciseTypeId, initialValues.exerciseSpecificId]);

  // Feedback HTML is stored in blob storage now, not returned inline with the Score DTO — fetch it on demand.
  useEffect(() => {
    if (!formData.id) return;
    getFeedback(formData.id)
      .then((feedback) => setFormData((prev) => ({ ...prev, feedback })))
      .catch(() => { });
  }, [formData.id]);

  // Set courseId when course is provided
  useEffect(() => {
    if (course?.id && formData.courseId !== course.id) {
      setFormData((prev) => ({
        ...prev,
        courseId: course.id,
        course: course,
      }));
    }
  }, [course]);

  // Auto-select activity type for Course Practical Evaluations
  useEffect(() => {
    if (scoreTypeConstant === ScoreTypeConstants.COURSE_PRACTICAL_EVALUATION && 
        course?.courseTypeId && 
        activityTypesData.length > 0 && 
        !formData.activityTypeId) {
      
      let autoSelectedActivityTypeId: string | undefined;
      
      if (course.courseTypeId === CourseTypeConstants.DEDICATED_HUNTER) {
        autoSelectedActivityTypeId = ActivityTypeConstants.HUNT_RELATED;
      } else if (course.courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON) {
        autoSelectedActivityTypeId = ActivityTypeConstants.SPORT_SHOOTING;
      }
      
      if (autoSelectedActivityTypeId) {
        setFormData((prev) => ({
          ...prev,
          activityTypeId: autoSelectedActivityTypeId,
        }));
      }
    }
  }, [scoreTypeConstant, course, activityTypesData, formData.activityTypeId]);

  // Auto-select activity type for QR-deep-linked competition entries — postal competition
  // shoots are always sport shooting activities.
  useEffect(() => {
    if (isQrCompetitionEntry && activityTypesData.length > 0 && !formData.activityTypeId) {
      setFormData((prev) => ({
        ...prev,
        activityTypeId: ActivityTypeConstants.SPORT_SHOOTING,
      }));
    }
  }, [isQrCompetitionEntry, activityTypesData, formData.activityTypeId]);

  useEffect(() => {
    const fetchFirearmSpecific = async () => {
      const memberFirearmId = formData.memberFirearmId;

      if (!memberFirearmId) {
        // In a QR-deep-linked competition entry, Exercise Type/Specific are locked to the
        // scanned target independent of firearm selection — don't clear them just because no
        // firearm is chosen yet.
        if (!isQrCompetitionEntry) {
          setExerciseTypes([]);
          setExerciseSpecifics([]);
        }
        setFirearmSpecific(undefined);
        setSelectedFirearm(undefined);
        setFilteredExerciseSpecifics([]);
        setSelectedFirearmSpecificId(undefined);
        return;
      }

      const memberFirearm = memberFirearms.find(
        (f) => f.id === memberFirearmId,
      );
      if (!memberFirearm) return;

      setSelectedFirearm(memberFirearm);

      // Trigger TQ hook to fetch firearm specific with exercises
      if (memberFirearm.firearmSpecificId) {
        setSelectedFirearmSpecificId(memberFirearm.firearmSpecificId);
      } else {
        setSelectedFirearmSpecificId(undefined);
      }
    };

    fetchFirearmSpecific();
  }, [formData.memberFirearmId, memberFirearms, isQrCompetitionEntry]);

  // React to firearm specific data changes from TQ hook
  useEffect(() => {
    if (!firearmSpecificWithExercises) return;

    setFirearmSpecific(firearmSpecificWithExercises);

    // In a QR-deep-linked competition entry, Exercise Type/Specific are locked to the target
    // that was scanned — don't let firearm selection replace that with the firearm's full list.
    if (isQrCompetitionEntry) return;

    if (firearmSpecificWithExercises.exercises) {
      if (exerciseTypeFilter && selectedFirearm) {
        // Use the filter that returns both types and specifics
        const filtered = exerciseTypeFilter(
          firearmSpecificWithExercises,
          course,
          selectedFirearm,
        );
        setExerciseTypes(filtered.exerciseTypes);
        setFilteredExerciseSpecifics(filtered.exerciseSpecifics);
      } else {
        // Default behavior for ScoreForm (no filtering)
        const types = firearmSpecificWithExercises.exercises.map(
          (ex: any) => ex.exerciseType,
        );
        setExerciseTypes(types);
        setFilteredExerciseSpecifics([]); // No pre-filtering for regular scores
      }

      // Preserve existing selections when editing
      if (formData.id && formData.exerciseTypeId) {
        const isValidExerciseType = exerciseTypes.some(
          (type) => type.id === formData.exerciseTypeId,
        );
        if (!isValidExerciseType) {
          console.warn(
            'Selected exercise type is not valid for this firearm',
          );
        }
      }
    }
  }, [firearmSpecificWithExercises, exerciseTypeFilter, course, selectedFirearm, isQrCompetitionEntry]);

  useEffect(() => {
    // Locked to the scanned target's exercise — skip the firearm-driven recompute entirely.
    if (isQrCompetitionEntry) return;

    const exerciseTypeId = formData.exerciseTypeId;

    if (exerciseTypeId && firearmSpecific?.exercises) {
      const selectedExercise = firearmSpecific.exercises.find(
        (ex: any) => ex.exerciseTypeId === exerciseTypeId,
      );
      let specifics = selectedExercise?.exerciseSpecifics || [];

      // If we have pre-filtered specifics (from evaluation form), use those
      if (filteredExerciseSpecifics.length > 0) {
        // Only show specifics for the selected exercise type that are in the filtered list
        specifics = specifics.filter((specific: ExerciseSpecificDTO) =>
          filteredExerciseSpecifics.some(filtered => filtered.id === specific.id)
        );
      } else {
        // Regular filtering for non-evaluation forms
        if (
          formData.scoreTypeId === ScoreTypeConstants.COURSE_PRACTICAL_EVALUATION ||
          formData.scoreTypeId === ScoreTypeConstants.IN_HOUSE
        ) {
          specifics = specifics.filter(
            (specific: ExerciseSpecificDTO) =>
              (specific.isComplianceHunt === true || specific.isComplianceSport === true) &&
              !specific.isCompetition,
          );
        }
      }

      setExerciseSpecifics(specifics);

      // When exerciseSpecificId changes, update the full exerciseSpecific object
      if (formData.exerciseSpecificId) {
        const foundSpecific = specifics.find(
          (spec) => spec.id === formData.exerciseSpecificId,
        );
        if (foundSpecific && foundSpecific !== formData.exerciseSpecific) {
          setFormData((prev) => ({
            ...prev,
            exerciseSpecific: foundSpecific,
          }));
        }
      }
    } else if (!exerciseTypeId) {
      // Only clear the pending selection when the exercise type itself is unset (a deliberate
      // deselect, or no selection yet). If exerciseTypeId is set but firearmSpecific hasn't
      // loaded yet (e.g. a deep-linked prefill still waiting on firearm auto-selection), leave
      // any prefilled exerciseSpecificId alone — it will be resolved once firearmSpecific loads.
      setExerciseSpecifics([]);
      if (!formData.id) {
        setFormData((prev) => ({
          ...prev,
          exerciseSpecificId: undefined,
          exerciseSpecific: undefined,
        }));
      }
    }
  }, [
    isQrCompetitionEntry,
    formData.exerciseTypeId,
    formData.exerciseSpecificId,
    firearmSpecific?.exercises,
    formData.id,
    filteredExerciseSpecifics, // Add dependency
  ]);

  useEffect(() => {
    const fetchExerciseTemplate = async () => {
      if (formData.exerciseSpecific?.exerciseTemplateId) {
        setIsLoadingTemplate(true);
        try {
          const response = await getExerciseTemplateById(
            formData.exerciseSpecific.exerciseTemplateId,
          );
          const template = response?.data || null;
          setExerciseTemplate(template);

          if (template) {
            setFormData((prev) => ({
              ...prev,
              templateId: template.id,
            }));
          }
        } catch (error) {
          console.error('Error fetching exercise template:', error);
          setExerciseTemplate(null);
        } finally {
          setIsLoadingTemplate(false);
        }
      } else {
        setExerciseTemplate(null);

        // Only reset score data for NEW scores, not when editing
        if (!formData.id) {
          setFormData((prev) => ({
            ...prev,
            templateId: undefined,
            scoreSections: [],
            grandTotalScore: undefined,
            grandTotalPercentage: undefined,
            grandTotalMark: undefined,
          }));
        } else {
          // For existing scores, only clear templateId but preserve score data
          setFormData((prev) => ({
            ...prev,
            templateId: undefined,
          }));
        }
      }
    };

    fetchExerciseTemplate();
  }, [formData.exerciseSpecific?.exerciseTemplateId, formData.id]); // Add formData.id as dependency

  useEffect(() => {
    if (formData.activityTypeId && filteredActivityTypes.length > 0) {
      const isStillValid = filteredActivityTypes.some(
        (type) => type.id === formData.activityTypeId,
      );
      if (!isStillValid) {
        setFormData((prev) => ({
          ...prev,
          activityTypeId: undefined,
        }));
      }
    }
  }, []);

  // Audit logs effect
  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (initialValues.id) {
        try {
          const response = await searchAuditLogs({
            page: auditCurrentPage,
            pageSize: auditPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
            filters: {
              tableName: 'Score',
              recordId: initialValues.id,
            },
          });

          if (response && response?.success && response?.data) {
            setAuditLogs(response.data.results as AuditLogDTO[]);
            setAuditTotalPages(
              Math.ceil((response.data.totalCount ?? 0) / auditPageSize),
            );
          }
        } catch (error) {
          console.error('Error fetching audit logs:', error);
        }
      }
    };

    fetchAuditLogs();
  }, [initialValues.id, auditCurrentPage, auditPageSize]);

  // For a QR-deep-linked competition entry, only show firearms actually eligible for the
  // scanned target's exercise — a member shouldn't be able to pick an incompatible firearm.
  const visibleMemberFirearms = useMemo(() => {
    if (!isQrCompetitionEntry || eligibleFirearmIds === null) return memberFirearms;
    return memberFirearms.filter((f) => f.id && eligibleFirearmIds.includes(f.id));
  }, [memberFirearms, isQrCompetitionEntry, eligibleFirearmIds]);

  return {
    // State
    formData,
    setFormData,
    member,
    firearmSpecific,
    exerciseSpecifics,
    exerciseTemplate,
    isLoadingTemplate,
    selectedFirearm,
    isLockedExerciseSelection: isQrCompetitionEntry,

    // Dropdown data
    memberFirearms: visibleMemberFirearms,
    exerciseTypes,
    activityTypes: filteredActivityTypes,
    scoreStatuses,
    scoreTypes: scoreTypesData,

    // Audit
    auditLogs,
    auditCurrentPage,
    setAuditCurrentPage,
    auditTotalPages,
    auditPageSize,
    setAuditPageSize,

    // Loading states
    isLoadingMember,
    isLoadingMemberFirearms,
    isLoadingExerciseTypes,
    isLoadingActivityTypes,
    isLoadingScoreStatuses,
    isLoadingScoreTypes,
    isFirearmSpecific,
    isLoadingAuditLogs,

    // Validation
    errors,
    validate,

    // Derived states
    isActivityTypeDisabled,

    // Handlers
    handleFileChange,
    getFileIdForTarget,
    handleScoreDataChange,
    uploadingFiles,
  };
};
